// The practitioner-side transcription capture pipeline, shared by both consult pages (14ygtkj79zv S2).
import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";

import { noteActivity } from "../../lib/http/refresh";
import { AudioStreamingService } from "../../services/audioStreamingService";
import { ConsultAudioSettings, DEFAULT_CONSULT_AUDIO_SETTINGS, consultAudioConstraints, mergeConsultAudioSettings } from "../../utils/consult/audioSettings";
import { CaptureState, captureStateFor } from "./useConsultTranscripts";

// Long enough to be invisible, short enough that a momentary microphone conflict costs seconds of transcript rather than the rest of the consult.
export const TRANSCRIBER_WATCHDOG_MS = 15000;
// Well inside the refresh cycle, and a hidden tab's throttled interval still fires about once a minute.
export const CAPTURE_ACTIVITY_INTERVAL_MS = 60000;

export interface UseTranscriberCaptureOptions {
	roomId: string;
	roomType: "online" | "inperson";
	participantId: string;
	// false for a completed consult: nothing is ever built and the watchdog never runs.
	enabled: boolean;
}

export interface TranscriberCapture {
	consultationStarted: boolean;
	consultationStartedRef: MutableRefObject<boolean>;
	markConsultationStarted: () => void;
	ensureTranscriberRunning: () => Promise<boolean>;
	startCapture: () => Promise<boolean>;
	stopCapture: () => void;
	toggleTranscribeListener: () => Promise<void>;
	transcriberEnabled: boolean;
	transcriberActive: boolean;
	setTranscriberActive: Dispatch<SetStateAction<boolean>>;
	captureRunning: boolean;
	captureState: CaptureState | null;
	audioStreamingServiceRef: MutableRefObject<AudioStreamingService | null>;
	setAudioStreamingService: (service: AudioStreamingService | null) => void;
	selectedMicrophone: string;
	setSelectedMicrophone: Dispatch<SetStateAction<string>>;
	switchMicrophone: (deviceId: string) => Promise<boolean>;
	getCaptureTrack: () => MediaStreamTrack | null;
	audioSettings: ConsultAudioSettings;
	handleAudioSettingsChange: (incoming: Partial<ConsultAudioSettings>) => void;
	dispose: () => void;
}

export const useTranscriberCapture = ({ roomId, roomType, participantId, enabled }: UseTranscriberCaptureOptions): TranscriberCapture => {
	const [consultationStarted, setConsultationStarted] = useState<boolean>(false);
	// Once-only: guards the multiple code paths that can all mark the consultation started, and is never reset by a stop.
	const consultationStartedRef = useRef<boolean>(false);
	const [transcriberEnabled, setTranscriberEnabled] = useState<boolean>(true);
	const [transcriberActive, setTranscriberActive] = useState<boolean>(false);
	// Render mirror of audioStreamingServiceRef — a ref change re-renders nothing, and the point of the capture banner is that an absent service is visible.
	const [captureRunning, setCaptureRunning] = useState<boolean>(false);
	const [selectedMicrophone, setSelectedMicrophone] = useState<string>("");
	const [audioSettings, setAudioSettings] = useState<ConsultAudioSettings>(DEFAULT_CONSULT_AUDIO_SETTINGS);

	const audioStreamingServiceRef = useRef<AudioStreamingService | null>(null);
	const audioStreamRef = useRef<MediaStream | null>(null);

	// Every write to that ref goes through here — a hand-written `.current =` would drift the render mirror.
	const setAudioStreamingService = useCallback((service: AudioStreamingService | null) => {
		audioStreamingServiceRef.current = service;
		setCaptureRunning(!!service);
	}, []);

	// A hands-free consult has no pointer or key input, so a running capture counts as presence or the idle limit signs the practitioner out mid-consult.
	useEffect(() => {
		if (!captureRunning) return;
		noteActivity();
		const keepAlive = setInterval(noteActivity, CAPTURE_ACTIVITY_INTERVAL_MS);
		return () => clearInterval(keepAlive);
	}, [captureRunning]);

	// State mirrors for the callbacks below, which must keep their identity: the page's socket init effect depends on them and rebuilds the socket + mesh mid-consult when they change.
	const transcriberEnabledRef = useRef<boolean>(true);
	useEffect(() => {
		transcriberEnabledRef.current = transcriberEnabled;
	}, [transcriberEnabled]);
	const transcriberActiveRef = useRef<boolean>(false);
	useEffect(() => {
		transcriberActiveRef.current = transcriberActive;
	}, [transcriberActive]);
	// Mirrors the STATE, not the once-only ref above: a stopped consult must refuse the toggle even though it has been started.
	const consultationStartedStateRef = useRef<boolean>(false);
	useEffect(() => {
		consultationStartedStateRef.current = consultationStarted;
	}, [consultationStarted]);
	const selectedMicrophoneRef = useRef<string>("");
	useEffect(() => {
		selectedMicrophoneRef.current = selectedMicrophone;
	}, [selectedMicrophone]);
	const audioSettingsRef = useRef<ConsultAudioSettings>(DEFAULT_CONSULT_AUDIO_SETTINGS);

	// A null check alone is not enough: the start awaits getUserMedia and init(), so two concurrent callers can both pass it and build a second service over a live one — which doubles every final-audio packet.
	const transcriberStartRef = useRef<Promise<boolean> | null>(null);

	// The one place the transcription pipeline is built. Idempotent, never rejects.
	const ensureTranscriberRunning = useCallback((): Promise<boolean> => {
		if (!enabled) return Promise.resolve(false);
		if (audioStreamingServiceRef.current) return Promise.resolve(true);
		if (transcriberStartRef.current) return transcriberStartRef.current;
		if (!transcriberEnabledRef.current) return Promise.resolve(false);

		const attempt = (async (): Promise<boolean> => {
			if (!audioStreamRef.current) {
				try {
					audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
						audio: consultAudioConstraints(audioSettingsRef.current, selectedMicrophoneRef.current),
					});
				} catch {
					console.error("consult.transcriber.microphone_failed", { roomId });
					return false;
				}
			}
			if (audioStreamingServiceRef.current) return true;

			const audioService = new AudioStreamingService({
				stream: audioStreamRef.current,
				roomId,
				participantId,
				roomType,
				onVoiceDetected: setTranscriberActive,
				maxChunkSize: 10,
				voiceThreshold: audioSettingsRef.current.voiceThreshold,
				silenceTimeout: audioSettingsRef.current.silenceTimeout,
			});
			// init() reports whether the pipeline is genuinely live; assigning a dead one would make the header read "Recording" with nothing reaching the transcript.
			if ((await audioService.init()) === false) {
				audioService.stop();
				console.error("consult.transcriber.init_failed", { roomId });
				return false;
			}
			audioService.start();
			setAudioStreamingService(audioService);
			setTranscriberActive(true);
			return true;
		})();

		transcriberStartRef.current = attempt;
		attempt.finally(() => {
			if (transcriberStartRef.current === attempt) transcriberStartRef.current = null;
		});
		return attempt;
	}, [roomId, roomType, participantId, enabled, setAudioStreamingService]);

	// Self-healing capture: one getUserMedia rejection (the microphone momentarily held by another application) would otherwise leave the consult unrecorded until someone notices the banner.
	useEffect(() => {
		if (!consultationStarted || !enabled) return;

		let reported: string | null = null;
		const reportOnce = (event: string) => {
			if (reported === event) return;
			reported = event;
			console.warn(event, { roomId });
		};

		const watchdog = setInterval(() => {
			// Never override a deliberate off — the practitioner turned the transcriber off themselves.
			if (!transcriberEnabledRef.current) return;

			const service = audioStreamingServiceRef.current;
			if (!service) {
				reportOnce("consult.transcriber.watchdog.restarting");
				ensureTranscriberRunning();
				return;
			}
			// Capture built from an inbound offer has no user gesture behind it, so its AudioContext can come up suspended — a service that exists and captures nothing.
			if (service.isCapturing === false) {
				reportOnce("consult.transcriber.watchdog.rebuilding");
				service.stop();
				setAudioStreamingService(null);
				ensureTranscriberRunning();
				return;
			}
			reported = null;
		}, TRANSCRIBER_WATCHDOG_MS);

		return () => clearInterval(watchdog);
	}, [consultationStarted, enabled, roomId, ensureTranscriberRunning, setAudioStreamingService]);

	// The funnel every route into "started" passes through, including the inbound offer that follows a refresh-while-alone — the only route that reaches here with capture off.
	const markConsultationStarted = useCallback(() => {
		if (consultationStartedRef.current) return;
		consultationStartedRef.current = true;
		setConsultationStarted(true);
		ensureTranscriberRunning();
	}, [ensureTranscriberRunning]);

	// The capture half of a Start click; the page adds its own socket emits around it.
	const startCapture = useCallback(async (): Promise<boolean> => {
		consultationStartedRef.current = true;
		setConsultationStarted(true);
		// A fresh start, so any surviving service is torn down rather than reused.
		if (audioStreamingServiceRef.current) {
			audioStreamingServiceRef.current.stop();
			setAudioStreamingService(null);
		}
		const running = await ensureTranscriberRunning();
		if (!running) setTranscriberActive(false);
		return running;
	}, [ensureTranscriberRunning, setAudioStreamingService]);

	// consultationStartedRef deliberately stays set: a reconnect's roomInfo must not re-arm the microphone while the End wizard is open.
	const stopCapture = useCallback(() => {
		if (audioStreamingServiceRef.current) {
			audioStreamingServiceRef.current.stop();
			setAudioStreamingService(null);
		}
		audioStreamRef.current?.getTracks().forEach((track) => track.stop());
		// Stopped tracks can never carry audio again, so the next start must acquire the microphone afresh.
		audioStreamRef.current = null;
		setConsultationStarted(false);
		setTranscriberActive(false);
	}, [setAudioStreamingService]);

	const toggleTranscribeListener = useCallback(async () => {
		if (!consultationStartedStateRef.current) return;
		if (transcriberActiveRef.current) {
			audioStreamingServiceRef.current?.stop();
			setAudioStreamingService(null);
			setTranscriberActive(false);
			setTranscriberEnabled(false);
			return;
		}
		if (audioStreamingServiceRef.current) {
			audioStreamingServiceRef.current.stop();
			setAudioStreamingService(null);
		}
		// The ref still mirrors the OFF this branch is undoing — set it first or the helper's enabled check no-ops and the resume button does nothing.
		transcriberEnabledRef.current = true;
		setTranscriberEnabled(true);
		if (!(await ensureTranscriberRunning())) message.error("Failed to access microphone");
	}, [ensureTranscriberRunning, setAudioStreamingService]);

	// Stop before reopening: some Windows drivers refuse a second exclusive open of the same microphone.
	const switchMicrophone = useCallback(
		async (deviceId: string): Promise<boolean> => {
			selectedMicrophoneRef.current = deviceId;
			setSelectedMicrophone(deviceId);
			// A build already under way opened the previous device; let it land so it is torn down below.
			if (transcriberStartRef.current) await transcriberStartRef.current;
			const hadService = !!audioStreamingServiceRef.current;
			if (hadService) {
				audioStreamingServiceRef.current?.stop();
				setAudioStreamingService(null);
			}
			audioStreamRef.current?.getTracks().forEach((track) => track.stop());
			audioStreamRef.current = null;
			// A stopped consult or a deliberate off stays off; whatever builds next opens the new device.
			if (!enabled || !transcriberEnabledRef.current || !(hadService || consultationStartedStateRef.current)) return true;
			return ensureTranscriberRunning();
		},
		[enabled, ensureTranscriberRunning, setAudioStreamingService],
	);

	const getCaptureTrack = useCallback((): MediaStreamTrack | null => audioStreamRef.current?.getAudioTracks()[0] ?? null, []);

	const handleAudioSettingsChange = useCallback((incoming: Partial<ConsultAudioSettings>) => {
		const next = mergeConsultAudioSettings(audioSettingsRef.current, incoming);
		audioSettingsRef.current = next;
		setAudioSettings(next);
		// VAD tuning lands on the next animation frame. The three constraint toggles cannot: they belong to the microphone track and only reach the next getUserMedia call.
		audioStreamingServiceRef.current?.setVadOptions({ voiceThreshold: next.voiceThreshold, silenceTimeout: next.silenceTimeout });
	}, []);

	// The page's unmount half: called from its socket init cleanup, not from an effect here, so it runs in the same order it always did.
	const dispose = useCallback(() => {
		audioStreamingServiceRef.current?.stop();
		setAudioStreamingService(null);
		audioStreamRef.current?.getTracks().forEach((track) => track.stop());
		audioStreamRef.current = null;
	}, [setAudioStreamingService]);

	// Keyed on the capture SERVICE being absent, never on transcriberActive — that is voice detection, so a practitioner who is simply not speaking would raise the warning.
	const captureState = captureStateFor(consultationStarted, transcriberEnabled, captureRunning);

	return {
		consultationStarted,
		consultationStartedRef,
		markConsultationStarted,
		ensureTranscriberRunning,
		startCapture,
		stopCapture,
		toggleTranscribeListener,
		transcriberEnabled,
		transcriberActive,
		setTranscriberActive,
		captureRunning,
		captureState,
		audioStreamingServiceRef,
		setAudioStreamingService,
		selectedMicrophone,
		setSelectedMicrophone,
		switchMicrophone,
		getCaptureTrack,
		audioSettings,
		handleAudioSettingsChange,
		dispose,
	};
};
